// React
import React, { useEffect, useState } from 'react';

import { 
    Avatar, 
    Box, 
    Button, 
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    IconButton, 
    Paper, 
    Stack, 
    ToggleButton, 
    ToggleButtonGroup, 
    Tooltip, 
    Typography 
} from '@mui/material';

// MUI Icons
import { 
    ArrowBack,CalendarMonth, DeleteOutlined,EditOutlined, NavigateBeforeOutlined, NavigateNextOutlined,FormatListBulleted,SendOutlined,Person} from '@mui/icons-material';

// Utils
import { getWeek, getConsistentAvatarColor, formatCalendarWeekString } from '../../utils/index.js';

// Hooks
import { useAppTheme } from '../../hooks/useAppTheme.js';

// Styles
import { red, grey, blue, green, orange } from '@mui/material/colors/index.js';
import { darkTheme } from '../../styles/colorSchemes/index.js';


const WeeklyScheduleHeader = (props) => {
    const {
        selectedAthlete,
        selectedViewTab,
        onChangeViewTab,
        prepCreateWeeklySchedule,
        handleSend,
        selectedCalenderWeek,
        setSelectedCalenderWeek,
        changeCalenderWeek,
        highlightCurrentCalenderWeek,
        year,
        setYear,
        weeklySchedule,
        isEditable,
        onDeleteWeeklySchedule,
        isAthleteView = false,
        isParentView = false
    } = props;

    const { isDarkMode } = useAppTheme();

    const [openConfirmation, setOpenConfirmation] = useState(false);
    const [previousView, setPreviousView] = useState('calendarWeek');

    const validWeekNumber = React.useMemo(() => {
        if (!selectedCalenderWeek || isNaN(selectedCalenderWeek)) {
            return getWeek(new Date());
        }
        return selectedCalenderWeek;
    }, [selectedCalenderWeek]);

    const displayWeekString = React.useMemo(() => {
        if (validWeekNumber && year && !isNaN(validWeekNumber)) {
            return formatCalendarWeekString(validWeekNumber, year);
        }
        return '';
    }, [validWeekNumber, year]);

    // Dark Mode Helper Logic
    const currentDate = new Date();
    const currentWeek = getWeek(currentDate);
    const currentYear = currentDate.getFullYear();
    
    const isFuture = year > currentYear || (year === currentYear && validWeekNumber > currentWeek);
    const isPast = year < currentYear || (year === currentYear && validWeekNumber < currentWeek);
    const isCurrent = year === currentYear && validWeekNumber === currentWeek;

    useEffect(() => {
        if (selectedViewTab !== 'createWeeklySchedule') {
            setPreviousView(selectedViewTab);
        }
    }, [selectedViewTab]);

    useEffect(() => {
        if (!selectedCalenderWeek || isNaN(selectedCalenderWeek)) {
            setSelectedCalenderWeek(getWeek(new Date()));
        }
    }, [selectedCalenderWeek, setSelectedCalenderWeek]);

    const getFullName = (athlete) => {
        if (!athlete) return 'Kein Athlet ausgewählt';
        return `${athlete.profile?.lastName || ''}, ${athlete.profile?.firstName || ''}`;
    };

    const isDeleteDisabled = () => {
        if (!weeklySchedule) return true;
        return weeklySchedule.calendarWeek < getWeek(new Date());
    };

    const getCurrentView = () => {
        if (selectedViewTab === "calendarWeek" || selectedViewTab === "createWeeklySchedule") {
            return "week";
        }
        return "year";
    };


    const handleViewChange = (event, newView) => {
        if (newView === null) return;
        
        if (newView === "week") {
            if (setYear) {
                const currentDate = new Date();
                setYear(currentDate.getFullYear());
                setSelectedCalenderWeek(getWeek(currentDate));
            }
            if (selectedViewTab === "createWeeklySchedule") {
                onChangeViewTab("calendarWeek");
            } else {
                onChangeViewTab("calendarWeek");
            }
        } else {
            if (setYear) {
                setYear(new Date().getFullYear());
            }
            onChangeViewTab("calendarYear");
        }
    };

    const handleBackButton = () => {
        if (previousView === 'calendarYear' && setYear) {
            const currentDate = new Date();
            setYear(currentDate.getFullYear());
            setSelectedCalenderWeek(getWeek(currentDate));
        }
        onChangeViewTab(previousView);
    };
    if (!selectedAthlete) {
        return (
            <Paper
                elevation={0}
                sx={{
                    borderRadius: 0,
                    borderBottom: '1px solid',
                    borderColor: isDarkMode ? darkTheme.cardBorder : 'divider',
                    bgcolor: isDarkMode ? '#1a1a1a' : 'background.paper'
                }}
            >
                <Box sx={{
                    px: { xs: 2, sm: 3 },
                    py: { xs: 2, sm: 2.5 },
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}>
                    {selectedViewTab === 'createWeeklySchedule' ? (
                        <Button
                            variant="outlined"
                            startIcon={<ArrowBack />}
                            onClick={handleBackButton}
                            sx={{
                                textTransform: 'none',
                                fontWeight: 500,
                                borderColor: isDarkMode ? darkTheme.cardBorder : 'divider',
                                color: isDarkMode ? darkTheme.textPrimary : 'text.primary',
                                px: 2,
                                py: 0.75,
                                borderRadius: 3,
                                '&:hover': {
                                    bgcolor: isDarkMode ? darkTheme.cardHover : grey[100],
                                    borderColor: isDarkMode ? darkTheme.textSecondary : grey[400]
                                }
                            }}
                        >
                            Zurück
                        </Button>
                    ) : (
                        <Box />
                    )}

                    <Chip
                        variant="outlined"
                        label="Kein Athlet ausgewählt"
                        sx={{
                            fontSize: '0.95rem',
                            color: isDarkMode ? darkTheme.textSecondary : grey[600],
                            borderColor: isDarkMode ? darkTheme.cardBorder : undefined
                        }}
                    />
                </Box>
            </Paper>
        );
    }

    return (
        <Paper 
            elevation={0} 
            sx={{ 
                borderRadius: 0,
                borderBottom: '1px solid',
                borderColor: isDarkMode ? darkTheme.cardBorder : 'divider',
                bgcolor: isDarkMode ? '#1a1a1a' : 'background.paper'
            }}
        >
            {/* Row 1: Athlete Profile & Actions */}
            <Box sx={{ 
                px: { xs: 2, sm: 3 }, 
                pt: { xs: 2, sm: 2.5 }, 
                pb: { xs: 1.5, sm: 2 },
                display: 'flex', 
                flexDirection: { xs: 'column', md: 'row' },
                alignItems: { xs: 'flex-start', md: 'center' }, 
                justifyContent: 'space-between',
                gap: { xs: 1.5, sm: 2 }
            }}>
                {/* Left: Athlete Profile */}
                <Stack direction="row" spacing={{ xs: 1.5, sm: 2 }} alignItems="center">
                    <Avatar 
                        sx={{ 
                            width: { xs: 40, sm: 48 }, 
                            height: { xs: 40, sm: 48 }, 
                           bgcolor: getConsistentAvatarColor(selectedAthlete),
                           color: isDarkMode ? darkTheme.accordionBg : 'white',

                        }}
                    >
                        <Person sx={{ fontSize: { xs: 24, sm: 30 } }} />
                    </Avatar>
                    <Box>
                        <Typography 
                            variant="h5" 
                            fontWeight={700}
                            sx={{ 
                                color: isDarkMode ? darkTheme.textPrimary : 'text.primary',
                                lineHeight: 1.2,
                                fontSize: { xs: '1.1rem', sm: '1.5rem' }
                            }}
                        >
                            {getFullName(selectedAthlete)}
                        </Typography>
                        <Typography 
                            variant="body2" 
                            color={isDarkMode ? darkTheme.textSecondary : 'text.secondary'}
                            sx={{ mt: 0.25, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                        >
                            Trainingsplan
                        </Typography>
                    </Box>
                </Stack>

                {/* Right: View Switcher & Action Button */}
                <Stack 
                    direction="row" 
                    spacing={{ xs: 1, sm: 2 }} 
                    alignItems="center"
                    sx={{ 
                        width: { xs: '100%', md: 'auto' },
                        justifyContent: { xs: 'space-between', md: 'flex-start' }
                    }}
                >
                    {/* Delete Button - Only shown in week view when editable and schedule exists */}
                    {isEditable && weeklySchedule && selectedViewTab === "calendarWeek" && !isAthleteView && !isParentView && (
                        <Tooltip title="Wochenplan löschen">
                            <span>
                                <IconButton
                                    onClick={() => setOpenConfirmation(true)}
                                    disabled={isDeleteDisabled()}
                                    sx={{
                                        color: red[600],
                                        transition: 'all 0.3s ease',
                                        '&:hover': {
                                            color: red[700],
                                            transform: 'scale(1.1)',
                                            bgcolor: 'transparent'
                                        },
                                        '&.Mui-disabled': {
                                            color: grey[400]
                                        }
                                    }}
                                >
                                    <DeleteOutlined />
                                </IconButton>
                            </span>
                        </Tooltip>
                    )}

                    {/* Back Button or View Switcher - Only show if not athlete/parent view */}
                    {!isAthleteView && !isParentView && (
                        selectedViewTab === "createWeeklySchedule" ? (
                            <Button
                                variant="outlined"
                                startIcon={<ArrowBack />}
                                onClick={handleBackButton}
                                sx={{
                                    textTransform: 'none',
                                    fontWeight: 500,
                                    borderColor: isDarkMode ? darkTheme.cardBorder : 'divider',
                                    color: isDarkMode ? darkTheme.textPrimary : 'text.primary',
                                    px: 2,
                                    py: 0.75,
                                    borderRadius: 3,
                                    '&:hover': {
                                        bgcolor: isDarkMode ? darkTheme.cardHover : grey[100],
                                        borderColor: isDarkMode ? darkTheme.textSecondary : grey[400]
                                    }
                                }}
                            >
                                Zurück
                            </Button>
                        ) : (
                            <ToggleButtonGroup
                                value={getCurrentView()}
                                exclusive
                                onChange={handleViewChange}
                                size="small"
                                sx={{
                                    borderRadius: 3,
                                    '& .MuiToggleButton-root': {
                                        px: 2,
                                        py: 0.75,
                                        textTransform: 'none',
                                        fontWeight: 500,
                                        border: '1px solid',
                                        borderColor: isDarkMode ? darkTheme.cardBorder : 'divider',
                                        color: isDarkMode ? darkTheme.textSecondary : undefined,
                                        borderRadius: 3,
                                        '&.Mui-selected': {
                                            bgcolor: isDarkMode ? darkTheme.accent : blue[600],
                                            color: isDarkMode ? '#1a1a1a' : 'white',
                                            borderColor: isDarkMode ? darkTheme.accent : blue[600],
                                            boxShadow: isDarkMode ? '0 4px 12px rgba(255, 152, 0, 0.4)' : '0 4px 12px rgba(25, 118, 210, 0.3)',
                                            '&:hover': {
                                                bgcolor: isDarkMode ? darkTheme.accentDark : blue[700],
                                                borderColor: isDarkMode ? darkTheme.accentDark : blue[700],
                                            }
                                        },
                                        '&:hover': {
                                            bgcolor: isDarkMode ? darkTheme.cardHover : undefined
                                        }
                                    }
                                }}
                            >
                                <ToggleButton value="week">
                                <FormatListBulleted sx={{ fontSize: 18, mr: 0.75, strokeWidth: 1, stroke: 'currentColor' }} />
                                    Woche
                                </ToggleButton>
                                <ToggleButton value="year">
                                    <CalendarMonth sx={{ fontSize: 18, mr: 0.75 }} />
                                    Jahr
                                </ToggleButton>
                            </ToggleButtonGroup>
                        )
                    )}

                    {/* Action Button - Only show if not parent view */}
                    {!isParentView && (
                        selectedViewTab === "createWeeklySchedule" ? (
                            <Button
                                variant="contained"
                                startIcon={<SendOutlined />}
                                onClick={handleSend}
                                sx={{
                                    bgcolor: green[600],
                                    color: 'white',
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    px: { xs: 1.5, sm: 2.5 },
                                    py: { xs: 0.75, sm: 1 },
                                    borderRadius: 3,
                                    boxShadow: 2,
                                    minWidth: { xs: 'auto', sm: 220 },
                                    fontSize: { xs: '0.8rem', sm: '0.875rem' },
                                    '&:hover': {
                                        bgcolor: green[700],
                                        boxShadow: 4
                                    }
                                }}
                            >
                                Plan speichern
                            </Button>
                        ) : (
                            prepCreateWeeklySchedule && (
                                <Button
                                    variant="contained"
                                    startIcon={<EditOutlined />}
                                    onClick={() => prepCreateWeeklySchedule()}
                                    sx={{
                                        bgcolor: isDarkMode ? darkTheme.accent : blue[600],
                                        color: isDarkMode ? '#1a1a1a' : 'white',
                                        textTransform: 'none',
                                        fontWeight: 600,
                                        px: { xs: 1.5, sm: 2.5 },
                                        py: { xs: 0.75, sm: 1 },
                                        borderRadius: 3,
                                        boxShadow: isDarkMode ? '0 4px 12px rgba(255, 152, 0, 0.4)' : '0 4px 12px rgba(25, 118, 210, 0.3)',
                                        minWidth: { xs: 'auto', sm: 220 },
                                        fontSize: { xs: '0.8rem', sm: '0.875rem' },
                                        '&:hover': {
                                            bgcolor: isDarkMode ? darkTheme.accentDark : blue[700],
                                            boxShadow: isDarkMode ? '0 4px 14px rgba(255, 152, 0, 0.5)' : '0 4px 14px rgba(25, 118, 210, 0.4)'
                                        }
                                    }}
                                >
                                    Trainingseinheit erstellen
                                </Button>
                            )
                        )
                    )}
                </Stack>
            </Box>

            {/* Row 2: Week Navigation (only shown in week view) */}
            {(selectedViewTab === "calendarWeek" || selectedViewTab === "createWeeklySchedule") && (
                <Box sx={{ 
                    px: { xs: 2, sm: 3 }, 
                    pb: { xs: 2, sm: 2.5 },
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center'
                }}>
                    {/* Compact Week Selector - "Floating Capsule" Style */}
                    <Paper
                        elevation={0}
                        sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            border: '1px solid',
                            borderColor: isDarkMode ? darkTheme.cardBorder : 'divider',
                            borderRadius: '24px',
                            overflow: 'hidden',
                            bgcolor: isDarkMode ? darkTheme.headerBg : 'background.paper',
                            transition: 'all 0.2s ease',
                            width: { xs: '100%', sm: 'auto' },
                            maxWidth: { xs: '100%', sm: 'none' },
                            '&:hover': {
                                boxShadow: isDarkMode ? '0 2px 8px rgba(0, 0, 0, 0.3)' : '0 2px 8px rgba(0, 0, 0, 0.08)',
                                borderColor: isDarkMode ? darkTheme.textSecondary : grey[400]
                            }
                        }}
                    >
                        {/* Previous Week Button */}
                        <IconButton
                            onClick={() => changeCalenderWeek(false)}
                            sx={{
                                borderRadius: 0,
                                px: { xs: 1, sm: 1.5 },
                                py: 0.75,
                                color: isDarkMode 
                                    ? (isFuture ? orange[500] : darkTheme.textSecondary)
                                    : highlightCurrentCalenderWeek(selectedCalenderWeek, "less"),
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                    bgcolor: isDarkMode ? 'rgba(255, 152, 0, 0.15)' : blue[50],
                                    color: isDarkMode ? orange[500] : undefined
                                }
                            }}
                        >
                            <NavigateBeforeOutlined sx={{ fontSize: { xs: 20, sm: 24 } }} />
                        </IconButton>

                        {/* Current Week Display - Clickable to jump to today */}
                        <Box
                            onClick={() => {
                                const currentDate = new Date();
                                if (setYear) {
                                    setYear(currentDate.getFullYear());
                                }
                                setSelectedCalenderWeek(getWeek(currentDate));
                            }}
                            sx={{
                                px: { xs: 2, sm: 3 },
                                py: 0.75,
                                cursor: 'pointer',
                                transition: 'all 0.2s ease',
                                flex: 1,
                                minWidth: 0,
                                '&:hover': {
                                    bgcolor: isDarkMode ? 'rgba(255, 152, 0, 0.1)' : blue[50]
                                }
                            }}
                        >
                            <Stack direction="row" spacing={{ xs: 0.5, sm: 1 }} alignItems="center" justifyContent="center">
                                <Typography 
                                    variant="body2" 
                                    fontWeight={600}
                                    sx={{ 
                                        color: isDarkMode 
                                            ? (isCurrent ? orange[500] : darkTheme.textPrimary)
                                            : highlightCurrentCalenderWeek(validWeekNumber, "equal"),
                                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                        whiteSpace: 'nowrap'
                                    }}
                                >
                                    KW {validWeekNumber}
                                </Typography>
                                <Typography 
                                    variant="body2" 
                                    color={isDarkMode ? darkTheme.textSecondary : "text.secondary"}
                                    sx={{ display: { xs: 'none', sm: 'block' } }}
                                >
                                    •
                                </Typography>
                                <Typography 
                                    variant="body2" 
                                    color={isDarkMode ? darkTheme.textSecondary : "text.secondary"}
                                    sx={{ 
                                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap'
                                    }}
                                >
                                    {displayWeekString || formatCalendarWeekString(validWeekNumber, year)}
                                </Typography>
                            </Stack>
                        </Box>

                        {/* Next Week Button */}
                        <IconButton
                            onClick={() => changeCalenderWeek(true)}
                            sx={{
                                borderRadius: 0,
                                px: { xs: 1, sm: 1.5 },
                                py: 0.75,
                                color: isDarkMode 
                                    ? (isPast ? orange[500] : darkTheme.textSecondary)
                                    : highlightCurrentCalenderWeek(selectedCalenderWeek, "more"),
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                    bgcolor: isDarkMode ? 'rgba(255, 152, 0, 0.15)' : blue[50],
                                    color: isDarkMode ? orange[500] : undefined
                                }
                            }}
                        >
                            <NavigateNextOutlined sx={{ fontSize: { xs: 20, sm: 24 } }} />
                        </IconButton>
                    </Paper>
                </Box>
            )}

            {/* Delete Confirmation Dialog */}
            {weeklySchedule && (
                <Dialog open={openConfirmation} onClose={() => setOpenConfirmation(false)}>
                    <DialogTitle>
                        Löschen bestätigen
                    </DialogTitle>

                    <DialogContent>
                        <DialogContentText>
                            Soll dieser Wochenplan
                            (KW {weeklySchedule.calendarWeek}: {formatCalendarWeekString(weeklySchedule.calendarWeek, weeklySchedule.year)})
                            gelöscht werden?
                        </DialogContentText>
                    </DialogContent>

                    <DialogActions>
                        <Button 
                            sx={{ 
                                color: red[600], 
                                '&:hover': { bgcolor: red[50] } 
                            }}
                            onClick={() => { 
                                setOpenConfirmation(false); 
                                if (onDeleteWeeklySchedule) {
                                    onDeleteWeeklySchedule();
                                }
                            }}
                        >       
                            Löschen
                        </Button>

                        <Button 
                            sx={{ 
                                color: 'text.primary', 
                                '&:hover': { bgcolor: grey[100] } 
                            }}
                            onClick={() => setOpenConfirmation(false)}
                        >
                            Abbrechen
                        </Button>
                    </DialogActions>
                </Dialog>
            )}
        </Paper>
    );
};

export default WeeklyScheduleHeader;
